'use client';

import { cn } from '@/lib/utils';

type SkeletonTone = 'default' | 'muted' | 'subtle';

interface SkeletonBlockProperties {
  className?: string;
  rounded?: string;
  shimmer?: boolean;
  showShimmer: boolean;
  tone?: SkeletonTone;
}

function SkeletonBlock({
  className,
  shimmer = true,
  tone = 'default',
  rounded = 'rounded-xl',
  showShimmer,
}: SkeletonBlockProperties) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'relative isolate overflow-hidden bg-muted/45',
        showShimmer && shimmer && 'animate-pulse',
        tone === 'muted' && 'bg-muted/30',
        tone === 'subtle' && 'bg-muted/20',
        rounded,
        className
      )}
    />
  );
}

const ACTION_SKELETON_KEYS = ['reply', 'retweet', 'like', 'share'] as const;

interface TweetSkeletonProperties {
  count?: number;
  showShimmer?: boolean;
  variant?: 'timeline' | 'card' | 'inline';
}

export function TweetSkeleton({ count = 1, variant = 'timeline', showShimmer = true }: TweetSkeletonProperties) {
  const isInline = variant === 'inline';
  const isCard = variant === 'card';

  const block = (properties: Omit<SkeletonBlockProperties, 'showShimmer'>) => (
    <SkeletonBlock {...properties} showShimmer={showShimmer} />
  );

  const renderActionRow = () => (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-2">
      {ACTION_SKELETON_KEYS.map((metricKey, metricIndex) => (
        <div key={metricKey} className="flex items-center gap-2">
          {block({ className: 'h-3.5 w-3.5', rounded: 'rounded-full', shimmer: false, tone: 'muted' })}
          {block({ className: 'h-3 w-12 sm:w-14', tone: 'muted', shimmer: metricIndex % 2 === 0 })}
        </div>
      ))}
    </div>
  );

  const renderText = (outerIndex: number) => (
    <div className="space-y-2">
      {block({ className: 'h-3 w-full' })}
      {block({ className: 'h-3 w-11/12' })}
      {outerIndex % 2 === 0 && block({ className: 'h-3 w-4/5' })}
      {block({ className: 'h-3 w-2/3 sm:w-1/2', tone: 'muted' })}
    </div>
  );

  const renderHeader = () => (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        {block({ className: 'h-3.5 w-32 sm:w-40' })}
        {block({ className: 'h-3 w-16 sm:w-20', tone: 'muted' })}
      </div>
      {block({ className: 'hidden h-3 w-12 sm:block', tone: 'muted' })}
    </div>
  );

  const renderInlineRow = (outerIndex: number) => (
    <article key={`skeleton-inline-${outerIndex}`} className="px-0 py-2">
      <div className="flex items-center gap-3">
        {block({ className: 'h-8 w-8', rounded: 'rounded-full' })}
        <div className="flex-1 min-w-0 space-y-1.5">
          {block({ className: 'h-3 w-2/3' })}
          {block({ className: 'h-3 w-1/2', tone: 'muted' })}
        </div>
      </div>
    </article>
  );

  const renderCardRow = (outerIndex: number) => (
    <article
      key={`skeleton-card-${outerIndex}`}
      className="relative overflow-hidden rounded-xl border border-border/60 bg-card p-4 shadow-sm"
    >
      <div className="flex gap-3">
        {block({ className: 'h-10 w-10 sm:h-12 sm:w-12', rounded: 'rounded-full' })}
        <div className="flex-1 min-w-0 space-y-3">
          {renderHeader()}
          {renderText(outerIndex)}
          {renderActionRow()}
        </div>
      </div>
    </article>
  );

  const renderTimelineRow = (outerIndex: number) => {
    const showMedia = outerIndex % 3 === 0;
    const showQuote = outerIndex % 4 === 2;

    return (
      <article key={`skeleton-timeline-${outerIndex}`} className="bg-transparent">
        <div className="px-3 sm:px-4 py-4 sm:py-5">
          <div className="flex gap-3 sm:gap-4">
            <div className="flex-shrink-0">
              {block({ className: 'h-10 w-10 sm:h-12 sm:w-12', rounded: 'rounded-full' })}
            </div>

            <div className="flex-1 min-w-0 space-y-3">
              {renderHeader()}
              {renderText(outerIndex)}

              {showMedia &&
                block({ className: 'h-[180px] w-full border border-border/40', rounded: 'rounded-xl', tone: 'subtle' })}

              {showQuote && (
                <div className="rounded-xl border border-border/45 bg-muted/15 p-3">
                  {block({ className: 'h-3 w-3/5', shimmer: false, tone: 'muted' })}
                  <div className="mt-2 space-y-2">
                    {block({ className: 'h-3 w-full', shimmer: false, tone: 'muted' })}
                    {block({ className: 'h-3 w-4/5', shimmer: false, tone: 'muted' })}
                  </div>
                </div>
              )}

              {!isInline && renderActionRow()}
            </div>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div
      className={cn(
        isCard && 'space-y-4',
        isInline && 'space-y-0',
        !(isCard || isInline) && 'divide-y divide-border/60'
      )}
    >
      {Array.from({ length: count }, (_unused, outerIndex) => {
        if (isCard) {
          return renderCardRow(outerIndex);
        }

        if (isInline) {
          return renderInlineRow(outerIndex);
        }

        return renderTimelineRow(outerIndex);
      })}
    </div>
  );
}

// Convenience wrapper for timeline loading states
export function TweetSkeletonShimmer({ count = 3 }: Pick<TweetSkeletonProperties, 'count'>) {
  return <TweetSkeleton count={count} variant="timeline" showShimmer={true} />;
}
